import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    Paper,
    Chip,
    IconButton,
    Avatar,
    AvatarGroup,
    LinearProgress,
    Badge,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Skeleton,
    Divider,
    List,
    ListItem,
    ListItemText,
    Menu,
    MenuItem,
    Tabs,
    Tab,
    TextField,
    CircularProgress
} from "@mui/material";
import {
    Add as AddIcon,
    Folder as FolderIcon,
    Extension as PuzzleIcon,
    CheckCircle as CheckCircleIcon,
    Launch as LaunchIcon,
    Delete as DeleteIcon,
    CloudUpload as CloudUploadIcon,
    InfoOutlined as InfoOutlinedIcon,
    MoreVert as MoreVertIcon,
    Warning as WarningIcon,
    Schedule as ScheduleIcon,
    NotificationsActive as NotificationsIcon,
    Close as CloseIcon,
    Edit as EditIcon,
    Download as DownloadIcon,
    Share as ShareIcon,
    DriveFileMove as MoveIcon,
    Upload as UploadIcon,
    AdsClick as AdsClickIcon,
    ExtensionSharp as ExtensionSharpIcon,
    FileCopy as FileCopyIcon,
    DriveFileRenameOutline as RenameIcon,
    AccountTree as OrganizeIcon,
    Info as InfoIcon,
    Description as DescriptionIcon
} from "@mui/icons-material";
import { Initiative, ProjectPrompt } from "../types";
import CreateProjectModal from "./CreateProjectModal";
import UpdatedProjectCard from "./UpdatedProjectCard";
import { ProjectSetupData } from "./AIProjectSetupModal";
import ProjectSetupChatbotModal from "./ProjectSetupChatbotModal";
import { setCurrentProjectApi } from "../../../api/api";
import { useFeatureFlag } from "@/features";
import { PlayCircleOutline as PlayCircleOutlineIcon, MenuBook as MenuBookIcon } from "@mui/icons-material";
import CTACard from "./CTACard";

import EnhancedProjectSetupChatbot from "../../interface/components/EnhancedProjectSetupChatbot";

interface ProjectsHubProps {
    onViewChange: (view: string) => void;
    initiatives: Initiative[];
    onCreateProject: (projectName: string) => void;
    selectedProject?: Initiative | null; // Changed to pass full object
    onSelectProject: (project: Initiative) => void; // Changed to pass full object
    onDeleteProject: (projectId: string) => void;
    onUpdateProject?: (projectId: string, updatedData: Partial<Initiative>) => void;
    onRefreshProjects?: () => void;
    token?: string | null;
    isLoadingProjects?: boolean; // New prop to track loading state
    onEditProduct: (productId: number) => void;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div role="tabpanel" hidden={value !== index} id={`project-tabpanel-${index}`} aria-labelledby={`project-tab-${index}`} {...other}>
            {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
        </div>
    );
}

const ProjectsHub: React.FC<ProjectsHubProps> = ({
    onViewChange,
    initiatives,
    onCreateProject,
    selectedProject,
    onSelectProject,
    onDeleteProject,
    onUpdateProject,
    onRefreshProjects,
    token,
    isLoadingProjects = false,
    onEditProduct
}) => {
    // Feature flag for edit product functionality
    const { isEnabled: isEditProductEnabled } = useFeatureFlag("edit-product");

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showChatbotSetup, setShowChatbotSetup] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [projectToDelete, setProjectToDelete] = useState<string | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [infoPanelOpen, setInfoPanelOpen] = useState(false);
    const [selectedInfoProject, setSelectedInfoProject] = useState<Initiative | null>(null);

    const [tabValue, setTabValue] = useState(0);
    const [editPromptDialog, setEditPromptDialog] = useState(false);
    const [editablePrompt, setEditablePrompt] = useState<ProjectPrompt | null>(null);
    const [editingCardId, setEditingCardId] = useState<string | null>(null);
    const [editingTitle, setEditingTitle] = useState("");

    // Loading state for new project creation
    const [isCreatingProject, setIsCreatingProject] = useState(false);
    const [creatingProjectName, setCreatingProjectName] = useState("");

    // Project attributes editing state
    const [showProjectAttributesWarning, setShowProjectAttributesWarning] = useState(false);
    const [isEditingProjectAttributes, setIsEditingProjectAttributes] = useState(false);
    const [editableProjectAttributes, setEditableProjectAttributes] = useState<ProjectPrompt | null>(null);

    // Auto-redirect disabled - users should manually select their project
    // This prevents confusion from automatic loading and allows users to choose which project to work on
    // useEffect(() => {
    //     if (initiatives.length === 1 && !selectedProject && !sessionStorage.getItem("hasVisitedProjects")) {
    //         sessionStorage.setItem("hasVisitedProjects", "true");
    //         // Project selection now handles navigation internally
    //         onSelectProject(initiatives[0]);
    //     }
    // }, [initiatives.length, selectedProject, onSelectProject]);

    const handleProjectClick = async (projectId: string) => {
        // Mark that user has explicitly navigated to features
        sessionStorage.setItem("hasVisitedProjects", "true");

        // Find the full project object
        const project = initiatives.find(p => p.id === projectId);
        if (project && token) {
            try {
                // Set the project as active using the stored procedure
                await setCurrentProjectApi(parseInt(projectId), token);
                // Then select the project
                onSelectProject(project);
            } catch (error) {
                console.error("Failed to set project as active:", error);
                // Still select the project even if setting active fails
                onSelectProject(project);
            }
        } else if (project) {
            // If no token, just select the project
            onSelectProject(project);
        }
    };

    const handleCreateProject = (projectName: string) => {
        onCreateProject(projectName);
        setShowCreateModal(false);
    };

    const handleAIProjectSetup = async (data: ProjectSetupData) => {
        // Use the project name from chatbot (stored in projectType field) or create from other data
        const projectName = data.projectType || "New Project";

        // Show skeleton loading
        setIsCreatingProject(true);
        setCreatingProjectName(projectName);
        setShowChatbotSetup(false);

        // Simulate 3 second loading with skeleton
        await new Promise(resolve => setTimeout(resolve, 3000));

        // Create the actual project
        onCreateProject(projectName);

        // Hide skeleton loading
        setIsCreatingProject(false);
        setCreatingProjectName("");

        // Log the comprehensive project setup data
        console.log("AI Project Setup Data:", {
            projectName: projectName,
            uploadedFiles: data.uploadedFiles?.length || 0,
            targetUser: data.targetUser,
            painPoints: data.painPoints,
            softwareSolution: data.softwareSolution,
            teamComposition: data.teamComposition
        });
    };

    const handleDeleteClick = (projectId: string, event: React.MouseEvent) => {
        event.stopPropagation();
        setProjectToDelete(projectId);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (projectToDelete) {
            setIsDeleting(true);
            try {
                await onDeleteProject(projectToDelete);
                setProjectToDelete(null);
                // Close info panel if deleted project was selected
                if (selectedInfoProject?.id === projectToDelete) {
                    setInfoPanelOpen(false);
                    setSelectedInfoProject(null);
                }
                // Clear selected project if it was deleted
                if (selectedProject?.id === projectToDelete) {
                    // This will be handled by parent component
                }
            } finally {
                setIsDeleting(false);
                setDeleteDialogOpen(false);
            }
        }
    };

    const handleInfoClick = (project: Initiative, event: React.MouseEvent) => {
        event.stopPropagation();
        setSelectedInfoProject(project);
        setInfoPanelOpen(true);
        setTabValue(0); // Reset to Project Overview tab
    };

    const handleUploadIconClick = (project: Initiative, event: React.MouseEvent) => {
        event.stopPropagation();
        setSelectedInfoProject(project);
        setInfoPanelOpen(true);
        setTabValue(1); // Open to Supporting Files tab
    };

    const handleRename = (projectId: string) => {
        const project = initiatives.find(p => p.id === projectId);
        if (project) {
            setEditingCardId(projectId);
            setEditingTitle(project.name);
        }
    };

    const handleSaveRename = (projectId: string) => {
        // Here you would update the project name in your state/backend
        console.log(`Renaming project ${projectId} to: ${editingTitle}`);
        setEditingCardId(null);
        setEditingTitle("");
    };

    const handleCancelRename = () => {
        setEditingCardId(null);
        setEditingTitle("");
    };

    const handleMakeCopy = (projectId: string) => {
        const project = initiatives.find(p => p.id === projectId);
        if (project) {
            const newProjectName = `${project.name} - Copy`;
            onCreateProject(newProjectName);
        }
    };

    const handleDownload = (projectId: string) => {
        // Implement download functionality
        console.log(`Downloading project: ${projectId}`);
    };

    const handleShare = (projectId: string) => {
        // Implement share functionality
        console.log(`Sharing project: ${projectId}`);
    };

    const handleOrganize = (projectId: string) => {
        // Implement organize functionality
        console.log(`Organizing project: ${projectId}`);
    };

    const handleMoveToTrash = (projectId: string) => {
        setProjectToDelete(projectId);
        setDeleteDialogOpen(true);
    };

    const handleEditProduct = (projectId: string, event: React.MouseEvent) => {
        event.stopPropagation();
        onEditProduct(parseInt(projectId));
    };

    const handleCloseInfoPanel = () => {
        setInfoPanelOpen(false);
        setSelectedInfoProject(null);
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const handleEditPrompt = () => {
        if (selectedInfoProject?.prompt) {
            setEditablePrompt({ ...selectedInfoProject.prompt });
            setEditPromptDialog(true);
        }
    };

    // Handle project attributes edit warning actions
    const handleProjectAttributesEditWarning = () => {
        setShowProjectAttributesWarning(true);
    };

    const handleEditExistingProject = () => {
        setShowProjectAttributesWarning(false);
        setIsEditingProjectAttributes(true);
        if (selectedInfoProject?.prompt) {
            setEditableProjectAttributes({ ...selectedInfoProject.prompt });
        }
    };

    const handleCreateNewProject = () => {
        setShowProjectAttributesWarning(false);
        onViewChange("new-product");
    };

    const handleCancelProjectAttributesEdit = () => {
        setIsEditingProjectAttributes(false);
        setEditableProjectAttributes(null);
    };

    const handleSaveProjectAttributes = () => {
        if (selectedInfoProject && editableProjectAttributes && onUpdateProject) {
            // Update the project with new attributes
            const updatedProject = {
                ...selectedInfoProject,
                prompt: editableProjectAttributes,
                name: editableProjectAttributes.projectType || selectedInfoProject.name,
                description: editableProjectAttributes.softwareSolution || selectedInfoProject.description
            };

            // Call the parent update handler
            onUpdateProject(selectedInfoProject.id, updatedProject);

            // Update local state
            setSelectedInfoProject(updatedProject);
            setIsEditingProjectAttributes(false);
            setEditableProjectAttributes(null);

            console.log("Project attributes updated successfully:", {
                projectId: selectedInfoProject.id,
                newAttributes: editableProjectAttributes
            });
        }
    };

    const getRiskColor = (risk: string) => {
        switch (risk) {
            case "low":
                return "#4caf50";
            case "medium":
                return "#ff9800";
            case "high":
                return "#f44336";
            default:
                return "#9e9e9e";
        }
    };

    const getDaysRemaining = (dueDate: string) => {
        const due = new Date(dueDate);
        const now = new Date();
        const diffTime = due.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    // Mock upload data for Table
    const getUploadsForProject = (project: Initiative) => {
        return (
            project.uploads?.map(upload => ({
                id: upload.id,
                name: upload.name,
                type: upload.type.split("/")[0] || "file",
                size: (upload.size / 1024 / 1024).toFixed(2) + " MB",
                tags: ["requirements", "design"],
                uploadDate: "2024-01-15"
            })) || []
        );
    };

    const AddProjectCard = () => (
        <Card
            sx={{
                width: "100%", // Fluid width that fills grid column
                height: 280, // Fixed height
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                cursor: isLoadingProjects ? "not-allowed" : "pointer",
                border: "2px dashed",
                borderColor: isLoadingProjects ? "action.disabled" : "primary.main",
                bgcolor: "background.paper",
                opacity: isLoadingProjects ? 0.5 : 1,
                transition: "all 0.3s ease",
                "&:hover": {
                    borderColor: isLoadingProjects ? "action.disabled" : "primary.dark",
                    bgcolor: isLoadingProjects ? "background.paper" : "#edf2ff", // Light blue active color - matches project cards
                    transform: isLoadingProjects ? "none" : "translateY(-2px)",
                    boxShadow: theme => (isLoadingProjects ? theme.shadows[1] : theme.shadows[4])
                }
            }}
            onClick={() => !isLoadingProjects && setShowChatbotSetup(true)}
        >
            <CardContent
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    textAlign: "center",
                    p: 2, // 16px padding inside card
                    width: "100%"
                }}
            >
                <Box
                    sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "50%",
                        bgcolor: isLoadingProjects ? "action.disabled" : "primary.main",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 2
                    }}
                >
                    <AddIcon sx={{ fontSize: 32, color: "white" }} />
                </Box>
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 600,
                        color: isLoadingProjects ? "text.disabled" : "primary.main",
                        mb: 1
                    }}
                >
                    Add Product
                </Typography>
                <Typography
                    variant="body2"
                    color={isLoadingProjects ? "text.disabled" : "text.secondary"}
                    sx={{
                        fontSize: "0.875rem",
                        lineHeight: 1.4
                    }}
                >
                    {isLoadingProjects ? "Loading existing products..." : "Create a new product to organize your features and work products"}
                </Typography>
            </CardContent>
        </Card>
    );

    // Skeleton loading card for project creation
    const ProjectCreationSkeleton = () => (
        <Card
            sx={{
                width: "100%",
                height: 280,
                display: "flex",
                flexDirection: "column",
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider"
            }}
        >
            <CardContent sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}>
                {/* Project Icon Skeleton */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                    <Skeleton variant="circular" width={40} height={40} />
                    <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="60%" height={24} />
                        <Skeleton variant="text" width="40%" height={16} />
                    </Box>
                </Box>

                {/* Progress Bar Skeleton */}
                <Box sx={{ mb: 2 }}>
                    <Skeleton variant="text" width="30%" height={16} />
                    <Skeleton variant="rectangular" width="100%" height={8} sx={{ borderRadius: 1, mt: 1 }} />
                </Box>

                {/* Features Skeleton */}
                <Box sx={{ mb: 2 }}>
                    <Skeleton variant="text" width="40%" height={16} />
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
                        <Skeleton variant="rectangular" width={60} height={20} sx={{ borderRadius: 1 }} />
                        <Skeleton variant="rectangular" width={50} height={20} sx={{ borderRadius: 1 }} />
                        <Skeleton variant="rectangular" width={70} height={20} sx={{ borderRadius: 1 }} />
                    </Box>
                </Box>

                {/* Team Members Skeleton */}
                <Box sx={{ mt: "auto", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <Skeleton variant="circular" width={24} height={24} />
                        <Skeleton variant="circular" width={24} height={24} />
                        <Skeleton variant="circular" width={24} height={24} />
                    </Box>
                    <Skeleton variant="text" width="30%" height={16} />
                </Box>

                {/* Creating indicator */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mt: 2, gap: 1 }}>
                    <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>
                        Creating "{creatingProjectName}"...
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );

    // Skeleton loading card for initial projects load
    const ProjectLoadingSkeleton = () => (
        <Card
            sx={{
                width: "100%",
                height: 280,
                display: "flex",
                flexDirection: "column",
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider"
            }}
        >
            <CardContent sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}>
                {/* Project Icon Skeleton */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                    <Skeleton variant="circular" width={40} height={40} />
                    <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="60%" height={24} />
                        <Skeleton variant="text" width="40%" height={16} />
                    </Box>
                </Box>

                {/* Progress Bar Skeleton */}
                <Box sx={{ mb: 2 }}>
                    <Skeleton variant="text" width="30%" height={16} />
                    <Skeleton variant="rectangular" width="100%" height={8} sx={{ borderRadius: 1, mt: 1 }} />
                </Box>

                {/* Features Skeleton */}
                <Box sx={{ mb: 2 }}>
                    <Skeleton variant="text" width="40%" height={16} />
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
                        <Skeleton variant="rectangular" width={60} height={20} sx={{ borderRadius: 1 }} />
                        <Skeleton variant="rectangular" width={50} height={20} sx={{ borderRadius: 1 }} />
                        <Skeleton variant="rectangular" width={70} height={20} sx={{ borderRadius: 1 }} />
                    </Box>
                </Box>

                {/* Team Members Skeleton */}
                <Box sx={{ mt: "auto", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <Skeleton variant="circular" width={24} height={24} />
                        <Skeleton variant="circular" width={24} height={24} />
                        <Skeleton variant="circular" width={24} height={24} />
                    </Box>
                    <Skeleton variant="text" width="30%" height={16} />
                </Box>
            </CardContent>
        </Card>
    );

    const drawerWidth = 600;

    return (
        <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
            {/* Page Header - Standardized to 64px to match MUI AppBar */}
            <Paper
                elevation={0}
                sx={{
                    height: 64, // Standardized to 64px
                    minHeight: 64,
                    maxHeight: 64,
                    display: "flex",
                    alignItems: "center",
                    px: 3, // Keep 24px horizontal padding
                    borderBottom: 1,
                    borderColor: "divider",
                    bgcolor: "#F1F5FE",
                    position: "relative",
                    zIndex: 1,
                    flexShrink: 0 // Prevent height changes during layout adjustments
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 600,
                            color: "text.primary",
                            fontSize: "1.25rem" // Reduced from 1.5rem to fit 64px height better
                        }}
                    >
                        Products
                    </Typography>
                    <Typography
                        variant="h4"
                        sx={{
                            color: "text.disabled",
                            fontWeight: 300,
                            fontSize: "1.25rem" // Reduced from 1.5rem for consistency
                        }}
                    >
                        |
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: "text.secondary",
                            fontSize: "0.875rem", // Reduced from 1rem to fit better
                            fontWeight: 400
                        }}
                    >
                        Your hub for orchestrating product-driven innovation
                    </Typography>
                </Box>
            </Paper>

            {/* Main Content Area with Right Panel */}
            <Box
                sx={{
                    flex: 1,
                    overflow: "hidden",
                    display: "flex",
                    position: "relative"
                }}
            >
                {/* Projects Grid Container */}
                <Box
                    sx={{
                        flex: 1,
                        overflow: "auto",
                        px: 3, // 24px container padding on left/right
                        py: 3,
                        transition: "margin-right 0.3s ease",
                        marginRight: infoPanelOpen ? `${drawerWidth}px` : 0
                    }}
                >
                    <Box
                        sx={{
                            display: "grid",
                            gap: 2, // 16px gaps between cards
                            width: "100%",

                            // Exact CSS Media Queries with grid-template-columns
                            "@media (max-width: 599px)": {
                                gridTemplateColumns: "1fr" // 1 card per row (100%)
                            },
                            "@media (min-width: 600px) and (max-width: 1279px)": {
                                gridTemplateColumns: "repeat(2, 1fr)" // 2 cards per row (each = (100% - gap) ÷ 2)
                            },
                            "@media (min-width: 1280px) and (max-width: 1599px)": {
                                gridTemplateColumns: "repeat(3, 1fr)" // 3 cards per row (each = (100% - totalGaps) ÷ 3)
                            },
                            "@media (min-width: 1600px)": {
                                gridTemplateColumns: "repeat(4, 1fr)" // 4 cards per row (each = (100% - totalGaps) ÷ 4)
                            },

                            // Dynamic adjustment when right panel (600px) opens
                            // This creates automatic wrapping as available width decreases
                            ...(infoPanelOpen && {
                                "@media (min-width: 600px) and (max-width: 1279px)": {
                                    gridTemplateColumns: "1fr" // Collapse to 1 column when panel constrains small screens
                                },
                                "@media (min-width: 1280px) and (max-width: 1599px)": {
                                    gridTemplateColumns: "repeat(2, 1fr)" // Reduce to 2 columns when panel constrains medium screens
                                },
                                "@media (min-width: 1600px)": {
                                    gridTemplateColumns: "repeat(3, 1fr)" // Reduce to 3 columns when panel constrains large screens
                                }
                            })
                        }}
                    >
                        {/* Add Project Card - Always First */}
                        <Box sx={{ height: 280 }}>
                            <AddProjectCard />
                        </Box>

                        {/* Show skeleton cards when loading initial projects */}
                        {isLoadingProjects ? (
                            <>
                                {/* Show 3 skeleton cards as placeholders */}
                                {[1, 2, 3].map(index => (
                                    <Box key={`skeleton-${index}`} sx={{ height: 280 }}>
                                        <ProjectLoadingSkeleton />
                                    </Box>
                                ))}
                            </>
                        ) : (
                            <>
                                {/* Project Cards */}
                                {initiatives.map(project => (
                                    <Box key={project.id} sx={{ height: 280 }}>
                                        <UpdatedProjectCard
                                            project={project}
                                            onProjectClick={handleProjectClick}
                                            onInfoClick={handleInfoClick}
                                            onUploadIconClick={handleUploadIconClick}
                                            onRename={handleRename}
                                            onMakeCopy={handleMakeCopy}
                                            onDownload={handleDownload}
                                            onShare={handleShare}
                                            onOrganize={handleOrganize}
                                            onMoveToTrash={handleMoveToTrash}
                                            onDeleteProject={onDeleteProject}
                                            onEditProduct={isEditProductEnabled ? handleEditProduct : undefined}
                                            editingCardId={editingCardId}
                                            editingTitle={editingTitle}
                                            onEditingTitleChange={setEditingTitle}
                                            onSaveRename={handleSaveRename}
                                            onCancelRename={handleCancelRename}
                                            isSelected={selectedProject?.id === project.id}
                                        />
                                    </Box>
                                ))}

                                {/* Skeleton Loading Card - Show when creating project - Always Last */}
                                {isCreatingProject && (
                                    <Box sx={{ height: 280 }}>
                                        <ProjectCreationSkeleton />
                                    </Box>
                                )}
                            </>
                        )}
                    </Box>
                    {/* Getting Started CTAs */}
                    <Divider sx={{ mt: 4, mb: 4 }} />
                    <Box
                        sx={{
                            width: "100%",
                            display: "flex",
                            gap: 2,
                            alignItems: "stretch",
                            flexWrap: "wrap",
                            mb: 4
                        }}
                    >
                        <CTACard
                            icon={<PlayCircleOutlineIcon sx={{ fontSize: 24, color: "#103699" }} />}
                            title="Watch Our Getting Started Videos"
                            description="Learn the basics of building Products and Features in Product Studio with short, focused walkthroughs. These videos show real examples and help you get up to speed quickly."
                            linkText="View Videos"
                            linkUrl="https://intelligenic.ai/getting-started/"
                            ariaLabel="Watch getting started tutorial videos"
                        />
                        <CTACard
                            icon={<MenuBookIcon sx={{ fontSize: 24, color: "#103699" }} />}
                            title="Read the Getting Started Guide"
                            description="Explore our step-by-step guide covering setup, workflows, and best practices for using Product Studio. Perfect for new users or anyone wanting a deeper overview."
                            linkText="Open Guide"
                            linkUrl="https://intelligenic.ai/getting-started/"
                            ariaLabel="Read the getting started documentation guide"
                        />
                    </Box>
                </Box>

                {/* Right Info Panel - Positioned below header */}
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        width: infoPanelOpen ? drawerWidth : 0,
                        height: "100%",
                        bgcolor: "background.paper",
                        borderLeft: infoPanelOpen ? 1 : 0,
                        borderColor: "divider",
                        transition: "width 0.3s ease",
                        overflow: "hidden",
                        zIndex: 2
                    }}
                >
                    {selectedInfoProject && infoPanelOpen && (
                        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
                            {/* Header */}
                            <Box
                                sx={{
                                    p: 3,
                                    borderBottom: 1,
                                    borderColor: "divider",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-start",
                                    bgcolor: "#F1F5FE"
                                }}
                            >
                                <Box>
                                    <Typography variant="h5" fontWeight={600} gutterBottom>
                                        {selectedInfoProject.name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {selectedInfoProject.code}
                                    </Typography>
                                </Box>
                                <IconButton onClick={handleCloseInfoPanel} sx={{ color: "text.secondary" }} aria-label="Close Info Panel">
                                    <CloseIcon />
                                </IconButton>
                            </Box>

                            {/* Tabs */}
                            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                                <Tabs value={tabValue} onChange={handleTabChange} variant="fullWidth">
                                    <Tab label="Product Overview" />
                                    <Tab label="Supporting Files" />
                                    <Tab label="Product Attributes" />
                                </Tabs>
                            </Box>

                            {/* Tab Content */}
                            <Box sx={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column" }}>
                                <Box sx={{ px: 3, flex: 1 }}>
                                    {/* Project Overview Tab */}
                                    <TabPanel value={tabValue} index={0}>
                                        <Box sx={{ mb: 3 }}>
                                            <Typography variant="h6" gutterBottom>
                                                Overview
                                            </Typography>
                                            <Typography variant="body2" paragraph>
                                                {selectedInfoProject.description}
                                            </Typography>

                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="subtitle2" gutterBottom>
                                                    Progress
                                                </Typography>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={selectedInfoProject.progress}
                                                    sx={{ height: 8, borderRadius: 4, mb: 1 }}
                                                />
                                                <Typography variant="caption" color="text.secondary">
                                                    {selectedInfoProject.progress}% complete
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* Timeline */}
                                        <Box sx={{ mb: 3 }}>
                                            <Typography variant="h6" gutterBottom>
                                                Timeline
                                            </Typography>
                                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                                                <Box>
                                                    <Typography variant="caption" color="text.disabled">
                                                        Start Date
                                                    </Typography>
                                                    <Typography variant="body2">{selectedInfoProject.startDate}</Typography>
                                                </Box>
                                                <Box>
                                                    <Typography variant="caption" color="text.disabled">
                                                        Due Date
                                                    </Typography>
                                                    <Typography variant="body2">{selectedInfoProject.dueDate}</Typography>
                                                </Box>
                                            </Box>

                                            {selectedInfoProject.milestones && (
                                                <Box>
                                                    <Typography variant="subtitle2" gutterBottom>
                                                        Milestones
                                                    </Typography>
                                                    <List dense>
                                                        {selectedInfoProject.milestones.map(milestone => (
                                                            <ListItem key={milestone.id} sx={{ px: 0 }}>
                                                                <CheckCircleIcon
                                                                    sx={{
                                                                        mr: 1,
                                                                        fontSize: 16,
                                                                        color: milestone.completed ? "success.main" : "text.disabled"
                                                                    }}
                                                                />
                                                                <ListItemText
                                                                    primary={milestone.name}
                                                                    secondary={milestone.date}
                                                                    primaryTypographyProps={{ variant: "body2" }}
                                                                    secondaryTypographyProps={{ variant: "caption" }}
                                                                />
                                                            </ListItem>
                                                        ))}
                                                    </List>
                                                </Box>
                                            )}
                                        </Box>
                                    </TabPanel>

                                    {/* Supporting Files Tab */}
                                    <TabPanel value={tabValue} index={1}>
                                        <Box sx={{ mb: 3 }}>
                                            <Typography variant="h6" gutterBottom>
                                                Product Files
                                            </Typography>

                                            {selectedInfoProject.uploads && selectedInfoProject.uploads.length > 0 ? (
                                                <TableContainer component={Paper} variant="outlined">
                                                    <Table size="small">
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell>Name</TableCell>
                                                                <TableCell>Type</TableCell>
                                                                <TableCell>Size</TableCell>
                                                                <TableCell>Date</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {getUploadsForProject(selectedInfoProject).map(upload => (
                                                                <TableRow key={upload.id} hover>
                                                                    <TableCell>
                                                                        <Typography variant="body2" fontWeight={500}>
                                                                            {upload.name}
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Chip label={upload.type} size="small" variant="outlined" sx={{ fontSize: "0.7rem" }} />
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            {upload.size}
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            {upload.uploadDate}
                                                                        </Typography>
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            ) : (
                                                <Box
                                                    sx={{
                                                        textAlign: "center",
                                                        py: 4,
                                                        color: "text.secondary"
                                                    }}
                                                >
                                                    <CloudUploadIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                                                    <Typography variant="body2">No files uploaded yet</Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </TabPanel>

                                    {/* Project Parameters Tab */}
                                    <TabPanel value={tabValue} index={2}>
                                        <Box sx={{ mb: 3 }}>
                                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                                                <Typography variant="h6">Project Parameters</Typography>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<EditIcon />}
                                                    onClick={() => {
                                                        setShowChatbotSetup(true);
                                                    }}
                                                    disabled={false} //!selectedInfoProject.prompt}
                                                >
                                                    Edit
                                                </Button>
                                            </Box>

                                            {/* Parent Field */}
                                            <Box sx={{ mb: 3 }}>
                                                <Typography variant="subtitle2" gutterBottom color="primary">
                                                    Parent
                                                </Typography>
                                                <TextField
                                                    fullWidth
                                                    value={selectedInfoProject.name}
                                                    disabled
                                                    variant="outlined"
                                                    helperText="Projects are top-level entities and serve as their own parent"
                                                    sx={{ "& .MuiOutlinedInput-root": { bgcolor: "action.hover" } }}
                                                />
                                            </Box>

                                            {selectedInfoProject.prompt ? (
                                                <Box sx={{ "& > *": { mb: 3 } }}>
                                                    <Box>
                                                        <Typography variant="subtitle2" gutterBottom color="primary">
                                                            Project Type
                                                        </Typography>
                                                        {isEditingProjectAttributes ? (
                                                            <TextField
                                                                fullWidth
                                                                value={
                                                                    editableProjectAttributes?.isUpgradeOrNew === "new"
                                                                        ? "New Development"
                                                                        : "Upgrade/Enhancement"
                                                                }
                                                                disabled
                                                                variant="outlined"
                                                                size="small"
                                                                helperText="Project type cannot be changed after creation"
                                                            />
                                                        ) : (
                                                            <Typography variant="body2">
                                                                {selectedInfoProject.prompt.isUpgradeOrNew === "new"
                                                                    ? "New Development"
                                                                    : "Upgrade/Enhancement"}
                                                            </Typography>
                                                        )}
                                                    </Box>

                                                    <Box>
                                                        <Typography variant="subtitle2" gutterBottom color="primary">
                                                            Target Users
                                                        </Typography>
                                                        {isEditingProjectAttributes ? (
                                                            <TextField
                                                                fullWidth
                                                                multiline
                                                                rows={2}
                                                                value={editableProjectAttributes?.targetUser || ""}
                                                                onChange={e =>
                                                                    setEditableProjectAttributes(prev =>
                                                                        prev ? { ...prev, targetUser: e.target.value } : null
                                                                    )
                                                                }
                                                                variant="outlined"
                                                                size="small"
                                                                placeholder="Describe the target users for this product"
                                                            />
                                                        ) : (
                                                            <Typography variant="body2">{selectedInfoProject.prompt.targetUser}</Typography>
                                                        )}
                                                    </Box>

                                                    <Box>
                                                        <Typography variant="subtitle2" gutterBottom color="primary">
                                                            Pain Points
                                                        </Typography>
                                                        {isEditingProjectAttributes ? (
                                                            <TextField
                                                                fullWidth
                                                                multiline
                                                                rows={3}
                                                                value={editableProjectAttributes?.painPoints || ""}
                                                                onChange={e =>
                                                                    setEditableProjectAttributes(prev =>
                                                                        prev ? { ...prev, painPoints: e.target.value } : null
                                                                    )
                                                                }
                                                                variant="outlined"
                                                                size="small"
                                                                placeholder="Describe the pain points this product addresses"
                                                            />
                                                        ) : (
                                                            <Typography variant="body2">{selectedInfoProject.prompt.painPoints}</Typography>
                                                        )}
                                                    </Box>

                                                    <Box>
                                                        <Typography variant="subtitle2" gutterBottom color="primary">
                                                            Software Solution
                                                        </Typography>
                                                        {isEditingProjectAttributes ? (
                                                            <TextField
                                                                fullWidth
                                                                multiline
                                                                rows={3}
                                                                value={editableProjectAttributes?.softwareSolution || ""}
                                                                onChange={e =>
                                                                    setEditableProjectAttributes(prev =>
                                                                        prev ? { ...prev, softwareSolution: e.target.value } : null
                                                                    )
                                                                }
                                                                variant="outlined"
                                                                size="small"
                                                                placeholder="Describe the software solution approach"
                                                            />
                                                        ) : (
                                                            <Typography variant="body2">{selectedInfoProject.prompt.softwareSolution}</Typography>
                                                        )}
                                                    </Box>

                                                    <Box>
                                                        <Typography variant="subtitle2" gutterBottom color="primary">
                                                            Team Composition
                                                        </Typography>
                                                        {isEditingProjectAttributes ? (
                                                            <TextField
                                                                fullWidth
                                                                multiline
                                                                rows={2}
                                                                value={editableProjectAttributes?.teamComposition || ""}
                                                                onChange={e =>
                                                                    setEditableProjectAttributes(prev =>
                                                                        prev ? { ...prev, teamComposition: e.target.value } : null
                                                                    )
                                                                }
                                                                variant="outlined"
                                                                size="small"
                                                                placeholder="Describe the team composition and roles"
                                                            />
                                                        ) : (
                                                            <Typography variant="body2">{selectedInfoProject.prompt.teamComposition}</Typography>
                                                        )}
                                                    </Box>

                                                    {selectedInfoProject.prompt.linkedFiles && selectedInfoProject.prompt.linkedFiles.length > 0 && (
                                                        <Box>
                                                            <Typography variant="subtitle2" gutterBottom color="primary">
                                                                Linked Files
                                                            </Typography>
                                                            <List dense>
                                                                {selectedInfoProject.prompt.linkedFiles.map((fileName, index) => (
                                                                    <ListItem key={index} sx={{ px: 0 }}>
                                                                        <FolderIcon sx={{ mr: 1, fontSize: 16, color: "text.secondary" }} />
                                                                        <ListItemText primary={fileName} primaryTypographyProps={{ variant: "body2" }} />
                                                                    </ListItem>
                                                                ))}
                                                            </List>
                                                        </Box>
                                                    )}

                                                    {/* Label Field */}
                                                    <Box>
                                                        <Typography variant="subtitle2" gutterBottom color="primary">
                                                            Labels
                                                        </Typography>
                                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                                            {/* Default project labels - in a real app these would come from project data */}
                                                            {["product", "development", selectedInfoProject.status].map((label, index) => (
                                                                <Chip
                                                                    key={index}
                                                                    label={label}
                                                                    size="small"
                                                                    variant="filled"
                                                                    sx={{
                                                                        fontSize: "0.75rem",
                                                                        height: 24,
                                                                        bgcolor: "primary.main",
                                                                        color: "primary.contrastText",
                                                                        "& .MuiChip-label": { px: 1 }
                                                                    }}
                                                                />
                                                            ))}
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            ) : (
                                                <Box
                                                    sx={{
                                                        textAlign: "center",
                                                        py: 4,
                                                        color: "text.secondary"
                                                    }}
                                                >
                                                    <DescriptionIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                                                    <Typography variant="body2">No product parameters available</Typography>
                                                </Box>
                                            )}

                                            {/* Save/Cancel buttons when editing */}
                                            {isEditingProjectAttributes && (
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        gap: 2,
                                                        justifyContent: "flex-end",
                                                        pt: 2,
                                                        borderTop: 1,
                                                        borderColor: "divider",
                                                        mt: 3
                                                    }}
                                                >
                                                    <Button variant="outlined" onClick={handleCancelProjectAttributesEdit}>
                                                        Cancel
                                                    </Button>
                                                    <Button variant="contained" onClick={handleSaveProjectAttributes} disabled={!editableProjectAttributes}>
                                                        Save Changes
                                                    </Button>
                                                </Box>
                                            )}
                                        </Box>
                                    </TabPanel>
                                </Box>

                                {/* Delete Project Button */}
                                <Box sx={{ p: 3, borderTop: 1, borderColor: "divider" }}>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        fullWidth
                                        startIcon={<DeleteIcon />}
                                        onClick={() => {
                                            if (selectedInfoProject) {
                                                handleDeleteClick(selectedInfoProject.id, new MouseEvent("click") as any);
                                            }
                                        }}
                                    >
                                        Delete Product
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Create Project Modal */}
            <CreateProjectModal open={showCreateModal} onClose={() => setShowCreateModal(false)} onSubmit={handleCreateProject} />

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <DialogTitle id="delete-dialog-title">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <WarningIcon color="warning" />
                        Delete Product
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete this product? This action cannot be undone. All associated features and work products will be
                        permanently removed.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        color="error"
                        variant="contained"
                        autoFocus
                        disabled={isDeleting}
                        startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : undefined}
                    >
                        {isDeleting ? "Deleting..." : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Project Setup Chatbot Modal */}
            {/* <ProjectSetupChatbotModal
        open={showChatbotSetup}
        onClose={() => setShowChatbotSetup(false)}
        onProjectSetup={handleAIProjectSetup}
      />
      */}

            <EnhancedProjectSetupChatbot open={showChatbotSetup} onClose={() => setShowChatbotSetup(false)} onRefresh={onRefreshProjects} />

            {/* Project Attributes Edit Warning Modal */}
            <Dialog
                open={showProjectAttributesWarning}
                onClose={() => setShowProjectAttributesWarning(false)}
                maxWidth="sm"
                fullWidth
                aria-labelledby="project-attributes-warning-title"
                aria-describedby="project-attributes-warning-description"
            >
                <DialogTitle id="project-attributes-warning-title">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <WarningIcon sx={{ color: "warning.main" }} />
                        Edit Product Attributes
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography id="project-attributes-warning-description" paragraph>
                        Modifying these product attributes might alter the existing product structure, including features and work products in Feature Studio.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        You can either edit the current product or create a new product with different attributes.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button onClick={() => setShowProjectAttributesWarning(false)} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={handleCreateNewProject} variant="outlined" startIcon={<AddIcon />}>
                        Create New Product
                    </Button>
                    <Button onClick={handleEditExistingProject} variant="contained" color="warning" startIcon={<EditIcon />}>
                        Edit Current Project
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ProjectsHub;
